/**
 * Marketing Settings tab — multi-channel ad account connection and configuration.
 * Supports Meta Ads and Google Ads account connections.
 */

import { useEffect, useState, useCallback } from 'react'
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Button,
  Input,
  Badge,
  Alert,
  AlertTitle,
  AlertDescription,
  Spinner,
} from '@/components/ui'
import {
  Eye,
  EyeOff,
  Trash2,
  Link,
  CheckCircle,
} from 'lucide-react'
import { useMarketing } from '@/hooks/useMarketing'
import { useToast } from '@/components/ui/Toast'
import { useFeature } from '@/hooks/useFeature'
import { CopilotPersonaSettings } from './CopilotPersonaSettings'

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString()
}

export function MarketingSettings() {
  const copilotEnabled = useFeature('marketing_copilot')
  const {
    fetchStatus,
    validateToken,
    connectAccount,
    disconnectAccount,
    account,
    hasAccount,
  } = useMarketing()
  const { addToast } = useToast()

  const [loading, setLoading] = useState(true)
  const [connecting, setConnecting] = useState(false)
  const [disconnecting, setDisconnecting] = useState(false)

  // Form state
  const [accessToken, setAccessToken] = useState('')
  const [adAccountId, setAdAccountId] = useState('')
  const [pixelId, setPixelId] = useState('')
  const [pageId, setPageId] = useState('')
  const [showToken, setShowToken] = useState(false)

  // Validation
  const [validating, setValidating] = useState(false)
  const [validationResult, setValidationResult] = useState<{
    valid: boolean
    userName?: string
    permissions: string[]
    missingPermissions: string[]
    adAccounts: Array<Record<string, unknown>>
  } | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      await fetchStatus()
    } catch {
      // silently handle
    } finally {
      setLoading(false)
    }
  }, [fetchStatus])

  useEffect(() => {
    load()
  }, [load])

  const handleValidate = async () => {
    if (!accessToken.trim() || !adAccountId.trim()) return
    setValidating(true)
    setValidationResult(null)
    try {
      const result = await validateToken({ accessToken, adAccountId })
      setValidationResult(result)
    } catch {
      addToast({ title: 'Error', description: 'Token validation failed', type: 'error' })
    } finally {
      setValidating(false)
    }
  }

  const handleConnect = async () => {
    if (!accessToken.trim() || !adAccountId.trim()) return
    setConnecting(true)
    try {
      await connectAccount({
        accessToken,
        adAccountId,
        pixelId: pixelId || undefined,
        pageId: pageId || undefined,
      })
      addToast({ title: 'Connected', description: 'Meta Ad Account connected', type: 'success' })
      setAccessToken('')
      setAdAccountId('')
      setPixelId('')
      setPageId('')
      setValidationResult(null)
    } catch (err) {
      addToast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Connection failed',
        type: 'error',
      })
    } finally {
      setConnecting(false)
    }
  }

  const handleDisconnect = async () => {
    setDisconnecting(true)
    try {
      await disconnectAccount()
      addToast({ title: 'Disconnected', description: 'Meta Ad Account disconnected', type: 'success' })
    } catch {
      addToast({ title: 'Error', description: 'Failed to disconnect', type: 'error' })
    } finally {
      setDisconnecting(false)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Current Account */}
      {hasAccount && account && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-success" />
              Connected Account
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <span className="text-muted-foreground">Ad Account ID:</span>
              <span className="font-mono">{account.adAccountId}</span>
              <span className="text-muted-foreground">Name:</span>
              <span>{account.name || '—'}</span>
              <span className="text-muted-foreground">Currency:</span>
              <span>{account.currency}</span>
              <span className="text-muted-foreground">Timezone:</span>
              <span>{account.timezone || '—'}</span>
              <span className="text-muted-foreground">Pixel ID:</span>
              <span className="font-mono">{account.pixelId || '—'}</span>
              <span className="text-muted-foreground">Page ID:</span>
              <span className="font-mono">{account.pageId || '—'}</span>
              <span className="text-muted-foreground">Sync Status:</span>
              <span>
                <Badge
                  variant={
                    account.syncStatus === 'error'
                      ? 'destructive'
                      : account.syncStatus === 'synced'
                        ? 'default'
                        : 'secondary'
                  }
                >
                  {account.syncStatus}
                </Badge>
              </span>
              {account.lastSyncedAt && (
                <>
                  <span className="text-muted-foreground">Last Synced:</span>
                  <span>{formatDate(account.lastSyncedAt)}</span>
                </>
              )}
              {account.syncError && (
                <>
                  <span className="text-muted-foreground">Sync Error:</span>
                  <span className="text-destructive">{account.syncError}</span>
                </>
              )}
            </div>
            <div className="pt-3 border-t">
              <Button
                variant="destructive"
                size="sm"
                onClick={handleDisconnect}
                disabled={disconnecting}
              >
                {disconnecting ? (
                  <Spinner size="sm" className="mr-2" />
                ) : (
                  <Trash2 className="w-4 h-4 mr-2" />
                )}
                Disconnect Account
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Connect New Account */}
      {!hasAccount && (
        <Card>
          <CardHeader>
            <CardTitle>Connect Meta Ad Account</CardTitle>
            <CardDescription>
              Enter your System User token and ad account ID to connect.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">System User Access Token *</label>
              <div className="flex gap-2">
                <Input
                  type={showToken ? 'text' : 'password'}
                  value={accessToken}
                  onChange={(e) => setAccessToken(e.target.value)}
                  placeholder="Paste your Meta System User token"
                />
                <Button variant="ghost" size="sm" onClick={() => setShowToken(!showToken)}>
                  {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Ad Account ID *</label>
              <Input
                value={adAccountId}
                onChange={(e) => setAdAccountId(e.target.value)}
                placeholder="act_123456789"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Pixel ID (optional)</label>
              <Input
                value={pixelId}
                onChange={(e) => setPixelId(e.target.value)}
                placeholder="For Conversions API"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Page ID (optional)</label>
              <Input
                value={pageId}
                onChange={(e) => setPageId(e.target.value)}
                placeholder="Facebook Page linked to WhatsApp"
              />
            </div>

            {/* Validation result */}
            {validationResult && (
              <Alert variant={validationResult.valid ? 'success' : 'destructive'}>
                <AlertTitle>{validationResult.valid ? 'Token Valid' : 'Invalid Token'}</AlertTitle>
                <AlertDescription>
                  {validationResult.userName && <p>User: {validationResult.userName}</p>}
                  {validationResult.missingPermissions.length > 0 && (
                    <p className="text-destructive">
                      Missing permissions: {validationResult.missingPermissions.join(', ')}
                    </p>
                  )}
                </AlertDescription>
              </Alert>
            )}

            <div className="flex gap-2 pt-2">
              <Button
                variant="secondary"
                onClick={handleValidate}
                disabled={validating || !accessToken.trim() || !adAccountId.trim()}
              >
                {validating && <Spinner size="sm" className="mr-2" />}
                Validate
              </Button>
              <Button
                onClick={handleConnect}
                disabled={connecting || !accessToken.trim() || !adAccountId.trim()}
              >
                {connecting ? (
                  <Spinner size="sm" className="mr-2" />
                ) : (
                  <Link className="w-4 h-4 mr-2" />
                )}
                Connect
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Required Permissions Info */}
      <Card>
        <CardHeader>
          <CardTitle>Required Permissions</CardTitle>
          <CardDescription>
            Your Meta System User token needs these permissions
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="space-y-1">
            {['ads_management', 'ads_read', 'business_management', 'pages_manage_ads'].map((perm) => (
              <li key={perm} className="flex items-center gap-2 text-sm">
                <CheckCircle className="w-3 h-3 text-muted-foreground" />
                <code className="text-xs bg-muted px-1 rounded-sm">{perm}</code>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      {/* ── Google Ads Connection ──────────────────────────────────────── */}
      <GoogleAdsConnectionSection />

      {/* ── Copilot persona (admin; rides GET/PUT /copilot/config) ─────── */}
      {copilotEnabled && <CopilotPersonaSettings />}
    </div>
  )
}


/**
 * Google Ads account connection sub-component.
 */
interface GoogleAdsAccount {
  id: string
  name?: string
  customerId: string
  managerId?: string
  currency: string
  isActive: boolean
  syncStatus: string
  syncError?: string | null
  lastSyncedAt?: string | null
  createdAt: string
}

interface DiscoveredAccount {
  id: string
  name: string
  currency: string
  timezone?: string
  is_test?: boolean
}

export function GoogleAdsConnectionSection() {
  const { addToast } = useToast()
  const [googleAccount, setGoogleAccount] = useState<GoogleAdsAccount | null>(null)
  const [loading, setLoading] = useState(true)
  const [syncing, setSyncing] = useState(false)
  const [disconnecting, setDisconnecting] = useState(false)

  // OAuth flow state
  const [oauthStep, setOauthStep] = useState<'idle' | 'authorizing' | 'selecting'>('idle')
  const [connecting, setConnecting] = useState(false)
  const [discoveredAccounts, setDiscoveredAccounts] = useState<DiscoveredAccount[]>([])
  const [pendingRefreshToken, setPendingRefreshToken] = useState('')
  const [selectedAccountId, setSelectedAccountId] = useState('')
  const [manualCustomerId, setManualCustomerId] = useState('')
  const [managerId, setManagerId] = useState('')
  const [accountName, setAccountName] = useState('')

  const fetchGoogleAccount = useCallback(async () => {
    try {
      const { apiClient } = await import('@/services/apiClient')
      const res = await apiClient.fetch('/api/marketing/google/account')
      if (res.ok) {
        const data = await res.json()
        if (data) {
          setGoogleAccount({
            id: data.id,
            name: data.name,
            customerId: data.customer_id,
            managerId: data.manager_id,
            currency: data.currency,
            isActive: data.is_active,
            syncStatus: data.sync_status,
            syncError: data.sync_error,
            lastSyncedAt: data.last_synced_at,
            createdAt: data.created_at,
          })
        }
      }
    } catch {
      // Google Ads feature may not be enabled
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchGoogleAccount() }, [fetchGoogleAccount])

  // Listen for OAuth callback from popup window
  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return
      if (event.data?.type !== 'google-oauth-callback') return

      const { code, error } = event.data
      if (error) {
        addToast({ title: 'Authorization Failed', description: error, type: 'error' })
        setOauthStep('idle')
        return
      }
      if (!code) {
        addToast({ title: 'Authorization Failed', description: 'No authorization code received', type: 'error' })
        setOauthStep('idle')
        return
      }

      // Exchange code for tokens via backend
      try {
        const { apiClient } = await import('@/services/apiClient')
        const redirectUri = `${window.location.origin}/auth/google/callback`
        const res = await apiClient.fetch('/api/marketing/google/callback', {
          method: 'POST',
          body: JSON.stringify({ code, redirect_uri: redirectUri }),
        })
        if (!res.ok) {
          const err = await res.json()
          throw new Error(err.detail || 'Token exchange failed')
        }
        const data = await res.json()
        if (!data.success) {
          throw new Error(data.error || 'Token exchange failed')
        }

        // Store refresh token and show account selection
        setPendingRefreshToken(data.refresh_token || '')
        setDiscoveredAccounts(data.accessible_accounts || [])
        setOauthStep('selecting')

        if (data.accessible_accounts?.length === 0) {
          addToast({
            title: 'No Accounts Found',
            description: 'No Google Ads accounts accessible with this authorization. Make sure your Google account has access to a Google Ads account.',
            type: 'error',
          })
          setOauthStep('idle')
        }
      } catch (err) {
        addToast({
          title: 'Connection Error',
          description: err instanceof Error ? err.message : 'Failed to exchange authorization code',
          type: 'error',
        })
        setOauthStep('idle')
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [addToast])

  const handleStartOAuth = async () => {
    setOauthStep('authorizing')
    try {
      const { apiClient } = await import('@/services/apiClient')
      const redirectUri = `${window.location.origin}/auth/google/callback`
      const res = await apiClient.fetch(`/api/marketing/google/auth-url?redirect_uri=${encodeURIComponent(redirectUri)}`)
      if (!res.ok) {
        const err = await res.json()
        throw new Error(err.detail || 'Failed to get authorization URL')
      }
      const data = await res.json()

      // Open popup for Google consent
      const width = 600
      const height = 700
      const left = window.screenX + (window.outerWidth - width) / 2
      const top = window.screenY + (window.outerHeight - height) / 2
      const popup = window.open(
        data.auth_url,
        'google-oauth',
        `width=${width},height=${height},left=${left},top=${top},popup=1`,
      )

      // Poll to detect if popup was closed without completing.
      // Google's consent page sets Cross-Origin-Opener-Policy which may
      // block access to popup.closed — catch and stop polling if so.
      if (popup) {
        const pollTimer = setInterval(() => {
          try {
            if (popup.closed) {
              clearInterval(pollTimer)
              setOauthStep(prev => prev === 'authorizing' ? 'idle' : prev)
            }
          } catch {
            // COOP policy blocked access — stop polling, rely on postMessage callback
            clearInterval(pollTimer)
          }
        }, 1000)
      }
    } catch (err) {
      addToast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to start authorization',
        type: 'error',
      })
      setOauthStep('idle')
    }
  }

  const handleConnectAccount = async () => {
    const effectiveCustomerId = manualCustomerId.trim() || selectedAccountId
    if (!effectiveCustomerId || !pendingRefreshToken) return
    setConnecting(true)
    try {
      const { apiClient } = await import('@/services/apiClient')
      const res = await apiClient.fetch('/api/marketing/google/connect', {
        method: 'POST',
        body: JSON.stringify({
          refresh_token: pendingRefreshToken,
          customer_id: effectiveCustomerId.replace(/-/g, ''),
          manager_id: managerId ? managerId.replace(/-/g, '') : undefined,
          name: accountName || undefined,
        }),
      })
      if (!res.ok) {
        const err = await res.json()
        throw new Error(err.detail || 'Connection failed')
      }
      addToast({ title: 'Connected', description: 'Google Ads account connected', type: 'success' })
      setOauthStep('idle')
      setPendingRefreshToken('')
      setDiscoveredAccounts([])
      setSelectedAccountId('')
      setManualCustomerId('')
      setManagerId('')
      setAccountName('')
      await fetchGoogleAccount()
    } catch (err) {
      addToast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Connection failed',
        type: 'error',
      })
    } finally {
      setConnecting(false)
    }
  }

  const handleSync = async () => {
    setSyncing(true)
    try {
      const { apiClient } = await import('@/services/apiClient')
      const res = await apiClient.fetch('/api/marketing/google/sync', { method: 'POST' })
      if (!res.ok) {
        const err = await res.json()
        throw new Error(err.detail || 'Sync failed')
      }
      const data = await res.json()
      addToast({
        title: 'Sync Complete',
        description: `${data.created} new, ${data.updated} updated campaigns`,
        type: 'success',
      })
      await fetchGoogleAccount()
    } catch (err) {
      addToast({
        title: 'Sync Error',
        description: err instanceof Error ? err.message : 'Sync failed',
        type: 'error',
      })
    } finally {
      setSyncing(false)
    }
  }

  const handleDisconnect = async () => {
    setDisconnecting(true)
    try {
      const { apiClient } = await import('@/services/apiClient')
      const res = await apiClient.fetch('/api/marketing/google/account', { method: 'DELETE' })
      if (!res.ok) throw new Error('Disconnect failed')
      addToast({ title: 'Disconnected', description: 'Google Ads account disconnected', type: 'success' })
      setGoogleAccount(null)
    } catch {
      addToast({ title: 'Error', description: 'Failed to disconnect', type: 'error' })
    } finally {
      setDisconnecting(false)
    }
  }

  if (loading) return null

  return (
    <>
      <div className="pt-2">
        <h2 className="text-lg font-semibold flex items-center gap-2 mb-4">
          <span className="text-blue-500">G</span> Google Ads
        </h2>
      </div>

      {googleAccount ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-success" />
              Google Ads Connected
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <span className="text-muted-foreground">Account Name:</span>
              <span>{googleAccount.name || '—'}</span>
              <span className="text-muted-foreground">Customer ID:</span>
              <span className="font-mono">{googleAccount.customerId}</span>
              {googleAccount.managerId && (
                <>
                  <span className="text-muted-foreground">Manager ID:</span>
                  <span className="font-mono">{googleAccount.managerId}</span>
                </>
              )}
              <span className="text-muted-foreground">Currency:</span>
              <span>{googleAccount.currency}</span>
              <span className="text-muted-foreground">Sync Status:</span>
              <span>
                <Badge
                  variant={
                    googleAccount.syncStatus === 'error'
                      ? 'destructive'
                      : googleAccount.syncStatus === 'synced'
                        ? 'default'
                        : 'secondary'
                  }
                >
                  {googleAccount.syncStatus}
                </Badge>
              </span>
              {googleAccount.lastSyncedAt && (
                <>
                  <span className="text-muted-foreground">Last Synced:</span>
                  <span>{formatDate(googleAccount.lastSyncedAt)}</span>
                </>
              )}
            </div>
            {googleAccount.syncStatus === 'error' && (
              <Alert variant="destructive">
                <AlertTitle>Reconnect required</AlertTitle>
                <AlertDescription>
                  {googleAccount.syncError ||
                    'The Google Ads connection stopped syncing. Disconnect and sign in with Google again.'}
                </AlertDescription>
              </Alert>
            )}
            <div className="flex gap-2 pt-3 border-t">
              <Button variant="secondary" size="sm" onClick={handleSync} disabled={syncing}>
                {syncing && <Spinner size="sm" className="mr-2" />}
                Sync Campaigns
              </Button>
              <Button variant="destructive" size="sm" onClick={handleDisconnect} disabled={disconnecting}>
                {disconnecting ? (
                  <Spinner size="sm" className="mr-2" />
                ) : (
                  <Trash2 className="w-4 h-4 mr-2" />
                )}
                Disconnect
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Connect Google Ads Account</CardTitle>
            <CardDescription>
              Sign in with your Google account to connect your Google Ads campaigns.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {oauthStep === 'idle' && (
              <Button onClick={handleStartOAuth}>
                <Link className="w-4 h-4 mr-2" />
                Sign in with Google
              </Button>
            )}

            {oauthStep === 'authorizing' && (
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <Spinner />
                Waiting for Google authorization...
              </div>
            )}

            {oauthStep === 'selecting' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Select Account</label>
                  <div className="space-y-2">
                    {discoveredAccounts.map((acct) => (
                      <label
                        key={acct.id}
                        className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                          selectedAccountId === acct.id
                            ? 'border-primary bg-primary/10'
                            : 'border-border hover:bg-muted'
                        }`}
                      >
                        <input
                          type="radio"
                          name="google-account"
                          value={acct.id}
                          checked={selectedAccountId === acct.id}
                          onChange={() => setSelectedAccountId(acct.id)}
                          className="accent-primary"
                        />
                        <div className="flex-1">
                          <div className="font-medium text-sm">{acct.name || `Account ${acct.id}`}</div>
                          <div className="text-xs text-muted-foreground">
                            ID: {acct.id} &middot; {acct.currency}
                            {acct.is_test && <Badge variant="secondary" className="ml-2">Test</Badge>}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Customer ID (manual entry)</label>
                  <Input
                    value={manualCustomerId}
                    onChange={(e) => setManualCustomerId(e.target.value)}
                    placeholder="e.g., 490-886-9976 (for sub-accounts not listed above)"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Enter a Customer ID to connect a sub-account managed by an MCC that isn't shown in the list above.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Manager Account ID (MCC)</label>
                  <Input
                    value={managerId}
                    onChange={(e) => setManagerId(e.target.value)}
                    placeholder="Required when connecting a sub-account"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Display Name (optional)</label>
                  <Input
                    value={accountName}
                    onChange={(e) => setAccountName(e.target.value)}
                    placeholder="My Google Ads Account"
                  />
                </div>
                <div className="flex gap-2 pt-2">
                  <Button variant="secondary" onClick={() => { setOauthStep('idle'); setDiscoveredAccounts([]); setPendingRefreshToken('') }}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleConnectAccount}
                    disabled={(!selectedAccountId && !manualCustomerId.trim()) || connecting}
                  >
                    {connecting ? (
                      <Spinner size="sm" className="mr-2" />
                    ) : (
                      <Link className="w-4 h-4 mr-2" />
                    )}
                    Connect Account
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </>
  )
}
